import { useTranslation } from "react-i18next";
import { useEffect, useRef } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      // Extract session_id from URL fragment
      const hash = location.hash;
      const match = hash.match(/session_id=([^&]+)/);
      
      if (!match) {
        toast.error("Authentication failed - no session ID");
        navigate("/login");
        return;
      }

      const sessionId = match[1];
      
      // Get user_type from query params (worker or client)
      // If user_type is present, this is a REGISTRATION from /register page
      // If not present, this is a LOGIN from /login page
      const userType = searchParams.get("user_type");
      const isRegistration = !!userType;

      try {
        let user;
        
        if (isRegistration) {
          // NEW USER - Use oauth-register endpoint
          const response = await axios.post(
            `${API}/auth/oauth-register`,
            { 
              session_id: sessionId,
              user_type: userType  // "worker" or "client"
            },
            { withCredentials: true }
          );
          user = response.data;
          
          // For workers that need approval
          if (user.role === "worker" && !user.is_approved) {
            toast.success("Account created! Please wait for admin approval.");
            navigate("/login", { replace: true });
            return;
          }
          
          toast.success("Account created successfully!");
        } else {
          // EXISTING USER - Use regular session endpoint (login only)
          const response = await axios.post(
            `${API}/auth/session`,
            { session_id: sessionId },
            { withCredentials: true }
          );
          user = response.data;
          toast.success(`Welcome back, ${user.name}!`);
        }
        
        setUser(user);

        // Redirect based on role
        const routes = {
          platform_admin: "/admin",
          operations_manager: "/admin",
          team_leader: "/team-leader",
          worker: "/worker",
          client_admin: "/client",
          client_viewer: "/client",
          webshop_admin: "/admin/webshop",
          rentals_admin: "/admin/rentals",
        };

        // Clear the hash from URL
        window.history.replaceState(null, "", window.location.pathname);
        
        navigate(routes[user.role] || "/client", { 
          replace: true,
          state: { user }
        });
      } catch (error) {
        console.error("OAuth callback error:", error);
        const errorMsg = error.response?.data?.detail || "Authentication failed";
        
        // If account not found during login, suggest registration
        if (error.response?.status === 404) {
          toast.error("Account not found. Please sign up first.");
          navigate("/register");
        } else if (error.response?.status === 400 && errorMsg.includes("already exists")) {
          toast.error("Account already exists. Please log in.");
          navigate("/login");
        } else {
          toast.error(errorMsg);
          navigate("/login");
        }
      }
    };

    processAuth();
  }, []);

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center">
      <div className="text-center">
        <div className="w-16 h-16 border-4 border-[#23F7DD] border-t-transparent rounded-full animate-spin mx-auto mb-6"></div>
        <p className="text-[#94A3B8] text-lg">Completing log in...</p>
      </div>
    </div>
  );
}
